/**
 * AllSourcesInterface Component
 *
 * Top-level interface for the All Sources page. Lists biblio sources of every
 * type (article, report, thesis, etc.) from the bibliographic sources database.
 *
 * Mirrors JournalArticlesInterface but has no secondary sidebar: source-type
 * filtering is done via a single-select Source Type dropdown in the table
 * toolbar (the backend supports one sourceType per query). Uses
 * AllSourcesContext for cached navigation, and reuses the generalized
 * BiblioSourceDetailsPanel (in read-only, source-oriented mode) for the inline
 * details panel.
 */

import { useEffect, useCallback, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { JournalArticle, sourceTypeLabelMap } from "@/services/journalArticlesService"
import { useAllSources } from "@/components/all-sources/AllSourcesContext"
import AllSourcesTable from "@/components/all-sources/AllSourcesTable"
import BiblioSourceDetailsPanel from "@/components/shared/BiblioSourceDetailsPanel"
import TableViewSelector, { ViewMode } from "@/components/shared/TableViewSelector"
import SearchButton from "@/components/shared/SearchButton"
import { getColumnGroupsForViewMode } from "@/components/all-sources/columns"
import { Spinner } from "@/components/ui/spinner.tsx"

/** Page title for the All Sources interface. */
const PAGE_TITLE = "All Sources"

/** Available page size options. */
const PAGE_SIZE_OPTIONS: number[] = [10, 25, 50, 100, 200]

/** Default page size (must match the provider default in AllSourcesContext). */
const DEFAULT_PAGE_SIZE: number = 50

/** Columns that are always visible regardless of the column selector. */
const fixedColumns = new Set<keyof JournalArticle>(["sourceId", "title"])

/** Valid view modes for URL param validation (All Sources offers compact + full). */
const VALID_VIEW_MODES: ViewMode[] = ["compact", "full"]

/**
 * Sentinel <Select> value for the "no source-type filter" option. A Select item
 * cannot use an empty-string value, so this stands in for "all types" and maps
 * to a null sourceType in context.
 */
const ALL_SOURCE_TYPES_VALUE = "__all__"

/**
 * All Sources interface component.
 *
 * Keeps the browser URL in sync with the context state and drives fetches when
 * search, the source-type filter, sort, or page size change.
 *
 * @returns The all sources interface component.
 */
export default function AllSourcesInterface(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    sources,
    pageIndex,
    pageSize,
    hasMore,
    loadedCount,
    searchQuery,
    sourceType,
    viewMode,
    visibleColumns,
    loading,
    error,
    lastFetchTime,
    selectedSource,
    fetchFirstPage,
    fetchNextPage,
    fetchPreviousPage,
    setPageSize,
    setSearchQuery,
    setSourceType,
    setViewMode,
    setVisibleColumns,
    selectSource,
    clearSelectedSource,
  } = useAllSources()

  // Guard so URL params are only applied to context once, on initial mount.
  const initialLoadDoneRef = useRef<boolean>(false)

  // ---------------------------------------------------------------------------
  // Initial mount: seed context state from URL query params, then fetch.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (initialLoadDoneRef.current) return
    initialLoadDoneRef.current = true

    // Page size
    const urlPageSize = searchParams.get("pageSize")
    if (urlPageSize) {
      const parsed = parseInt(urlPageSize, 10)
      if (PAGE_SIZE_OPTIONS.includes(parsed) && parsed !== pageSize) {
        setPageSize(parsed)
      }
    }

    // View mode
    const urlView = searchParams.get("view")
    if (urlView && VALID_VIEW_MODES.includes(urlView as ViewMode) && urlView !== viewMode) {
      setViewMode(urlView as ViewMode)
    }

    // Search
    const urlSearch = searchParams.get("search")
    if (urlSearch && urlSearch !== searchQuery) {
      setSearchQuery(urlSearch)
    }

    // Source-type filter. Only honour a value that is a known source-type key.
    const urlSourceType = searchParams.get("sourceType")
    if (
      urlSourceType &&
      Object.prototype.hasOwnProperty.call(sourceTypeLabelMap, urlSourceType) &&
      urlSourceType !== sourceType
    ) {
      setSourceType(urlSourceType)
    }

    // Keyset pagination has no shareable page token, so we always start at the
    // first page on load (no `page` URL param to seed). Only fetch if we don't
    // already have cached data from a previous visit.
    const hasCachedData = lastFetchTime !== null && sources.length > 0
    if (!hasCachedData) {
      fetchFirstPage()
    }
    // This effect intentionally runs only once on mount to seed state from the
    // URL; the values it reads are initial values and must not re-trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---------------------------------------------------------------------------
  // Re-fetch when search, filters, sort, or page size change (after mount).
  // ---------------------------------------------------------------------------
  const prevSearchRef = useRef(searchQuery)
  const prevSourceTypeRef = useRef(sourceType)
  const prevPageSizeRef = useRef(pageSize)

  useEffect(() => {
    if (!initialLoadDoneRef.current) return

    const searchChanged = prevSearchRef.current !== searchQuery
    const sourceTypeChanged = prevSourceTypeRef.current !== sourceType
    const pageSizeChanged = prevPageSizeRef.current !== pageSize

    prevSearchRef.current = searchQuery
    prevSourceTypeRef.current = sourceType
    prevPageSizeRef.current = pageSize

    if (searchChanged || sourceTypeChanged || pageSizeChanged) {
      fetchFirstPage()
    }
  }, [searchQuery, sourceType, pageSize, fetchFirstPage])

  // ---------------------------------------------------------------------------
  // Sync context state back to the URL query params.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const params: Record<string, string> = {}

    if (viewMode !== "compact") {
      params.view = viewMode
    }

    if (searchQuery) {
      params.search = searchQuery
    }

    const currentColumnGroups = getColumnGroupsForViewMode(viewMode)
    const currentDefaultKeys = currentColumnGroups.flatMap((g) => g.keys)
    const allCurrentVisible = currentDefaultKeys.every((k) => visibleColumns.has(k))
    if (!allCurrentVisible) {
      const relevantVisible = [...visibleColumns].filter((k) =>
        currentDefaultKeys.includes(k)
      )
      if (relevantVisible.length > 0) {
        params.columns = relevantVisible.join(",")
      }
    }

    // Note: the current page is intentionally NOT written to the URL. Keyset
    // pagination uses opaque forward-only cursors that are not meaningfully
    // shareable or restorable, so a deep link always opens on the first page.
    if (pageSize !== DEFAULT_PAGE_SIZE) {
      params.pageSize = String(pageSize)
    }

    if (sourceType !== null) {
      params.sourceType = sourceType
    }

    setSearchParams(params, { replace: true })
  }, [viewMode, visibleColumns, pageSize, setSearchParams, searchQuery, sourceType])

  /**
   * Navigate to the next page of results (follows the cursor). No-op when
   * there is no further page.
   */
  const handleNextPage = useCallback((): void => {
    if (!hasMore) return
    clearSelectedSource()
    fetchNextPage()
  }, [hasMore, clearSelectedSource, fetchNextPage])

  /**
   * Navigate to the previous page of results (uses the remembered cursor
   * stack). No-op on the first page.
   */
  const handlePreviousPage = useCallback((): void => {
    if (pageIndex === 0) return
    clearSelectedSource()
    fetchPreviousPage()
  }, [pageIndex, clearSelectedSource, fetchPreviousPage])

  /**
   * Change the number of rows per page. The change-detection effect resets to
   * the first page via fetchFirstPage.
   *
   * @param value - The selected page size as a string.
   */
  const handlePageSizeChange = useCallback(
    (value: string): void => {
      const parsed: number = parseInt(value, 10)
      if (!PAGE_SIZE_OPTIONS.includes(parsed)) return
      setPageSize(parsed)
      clearSelectedSource()
    },
    [setPageSize, clearSelectedSource]
  )

  /**
   * Handle a source-type dropdown change. The sentinel "all types" value maps
   * to a null filter. Resets to the first page via the change-detection effect.
   *
   * @param value - The selected source-type key, or the all-types sentinel.
   */
  const handleSourceTypeChange = useCallback(
    (value: string): void => {
      setSourceType(value === ALL_SOURCE_TYPES_VALUE ? null : value)
      clearSelectedSource()
    },
    [setSourceType, clearSelectedSource]
  )

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-destructive">Error: {error}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <title>{PAGE_TITLE} - MAP Apps Platform</title>
      <div className="flex flex-1 min-h-0">
        <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <CardTitle>
                  <h1>{PAGE_TITLE}</h1>
                </CardTitle>
                <CardDescription>
                  Browse all sources from the bibliographic sources database
                </CardDescription>
              </div>
              {/* Prominent, always-open search inline with the title. */}
              <div className="flex items-center gap-2 shrink-0">
                <SearchButton
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search all sources"
                  alwaysOpen
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col min-h-0">
            <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
              <div className="flex gap-1 flex-wrap">
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {loading ? (
                    <div className="flex align-middle gap-2">
                      <Spinner className="text-primary" />
                      Loading...
                    </div>
                  ) : loadedCount > 0 ? (
                    (() => {
                      // Keyset pagination has no exact total, so show the range
                      // loaded through the current page (e.g. "1–50 sources",
                      // with a trailing "+" while more pages remain).
                      const from: number = pageIndex * pageSize + 1
                      const to: number = loadedCount
                      const more: string = hasMore ? "+" : ""
                      return `${from.toLocaleString()}–${to.toLocaleString()}${more} sources`
                    })()
                  ) : (
                    "No sources"
                  )}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="xs"
                    className="p-1 ml-2 mr-1"
                    onClick={handlePreviousPage}
                    disabled={pageIndex === 0 || loading}
                    aria-label="Go to previous page"
                  >
                    <ChevronLeft className="size-4" /> Prev
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    className="p-1"
                    onClick={handleNextPage}
                    disabled={!hasMore || loading}
                    aria-label="Go to next page"
                  >
                    Next <ChevronRight className="size-4" />
                  </Button>
                </div>
              </div>

              {/* Right: rows-per-page, search, filters, view selector */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">Rows</span>
                  <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                    <SelectTrigger className="!h-[32px] w-[70px] text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={String(size)} className="text-sm">
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Select
                  value={sourceType ?? ALL_SOURCE_TYPES_VALUE}
                  onValueChange={handleSourceTypeChange}
                >
                  <SelectTrigger
                    className="!h-[32px] w-[180px] text-sm"
                    aria-label="Filter by source type"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_SOURCE_TYPES_VALUE} className="text-sm">
                      All Source Types
                    </SelectItem>
                    {Object.entries(sourceTypeLabelMap).map(([key, label]) => (
                      <SelectItem key={key} value={key} className="text-sm">
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <TableViewSelector
                  value={viewMode}
                  onChange={setViewMode}
                  options={["compact", "full"]}
                  columnGroups={getColumnGroupsForViewMode(viewMode)}
                  visibleKeys={visibleColumns}
                  fixedColumns={fixedColumns}
                  onSaveColumns={setVisibleColumns}
                />
              </div>
            </div>
            <AllSourcesTable
              sources={sources}
              loading={loading}
              onSourceSelect={selectSource}
              selectedSourceId={selectedSource?.sourceId}
              visibleColumns={visibleColumns}
            />
          </CardContent>
        </Card>

        {/* Inline details panel — appears on the right when a source is selected.
            Reuses the generalized journal-article panel in read-only,
            source-oriented mode (no topic editing, links to /all-sources). */}
        {selectedSource && (
          <BiblioSourceDetailsPanel
            article={selectedSource}
            onClose={clearSelectedSource}
            collections={[]}
            basePath="/all-sources"
            showTopicEditing={false}
            entityNoun="source"
          />
        )}
      </div>
    </>
  )
}
